/**
 * Per bond-type / mode general-information step config (Phase 9.8B).
 * Preserves legacy behavior differences across creation vs retrieval flows.
 */

const AUTHORIZATION_BANNER = {
  title: "Authorization to Obtain Underwriting Information",
  body:
    "Do you fully understand and agree, by submitting this request " +
    "for approval you are providing authorization for Probate Bond " +
    "Pros, surety companies and/or third-party brokers to verify " +
    "information you provided as well as permission to obtain your " +
    "consumer information from any source which would be necessary to " +
    "determine bonding eligibility? This includes obtaining credit " +
    "reports at time of application, during any review or renewal, at " +
    "the time of any potential or actual claim, and/or for any other " +
    "legitimate purposes determined by the surety in its reasonable " +
    "discretion. This information will be held in the strictest " +
    "confidence. Do you agree?",
};

const authorizationCreation = {
  threshold: 250000,
  useParseCurrency: true,
  requestForAuthorizationDefault: "",
  showEmptyAuthorizationOption: true,
};

const authorizationRetrieval250k = {
  threshold: 250000,
  useParseCurrency: false,
  requestForAuthorizationDefault: "No",
  showEmptyAuthorizationOption: false,
};

const authorizationGuardianshipRetrieval = {
  useGuardianshipRetrievalThreshold: true,
  useParseCurrency: false,
  requestForAuthorizationDefault: "No",
  showEmptyAuthorizationOption: false,
};

/** @typedef {{ name: string, label: string, defaultValue?: string, required?: boolean, showExplanationWhenYes?: boolean, explanationKey?: string }} YesNoQuestion */

/** @type {Record<string, string>} */
const vaExplanationDefaults = {
  ongoingBusiness: "",
  didLastWillAndTestament: "",
  demandedByInterestedParty: "",
  jointControlExercised: "",
  supplementalNeedsTrust: "",
  isDeclined: "",
};

/** @type {YesNoQuestion[]} */
const vaQuestions = [
  {
    name: "ongoingBusiness",
    label:
      "Will any of the business of the beneficiary be operated by the fiduciary?",
    showExplanationWhenYes: true,
  },
  {
    name: "didLastWillAndTestament",
    label: "Did beneficiary execute a last will and testament?",
  },
  {
    name: "indebtedGuardians",
    label: "Are any of the principals indebted to the Estate?",
    showExplanationWhenYes: true,
  },
  {
    name: "replacingFiduciary",
    label: "Does applicant replace any previous fiduciary?",
    showExplanationWhenYes: true,
  },
  {
    name: "replacingPriorBond",
    label: "Does this bond replace any prior bond?",
  },
  {
    name: "supplementalNeedsTrust",
    label: "Will a supplemental needs trust be created?",
  },
];

/** @type {YesNoQuestion[]} */
const guardianshipCreationQuestions = [
  {
    name: "ongoingBusiness",
    label:
      "Will any of the business of the ward be operated by the fiduciary?",
    showExplanationWhenYes: true,
  },
  {
    name: "didLastWillAndTestament",
    label: "Did ward execute a last will and testament?",
    required: false,
  },
  {
    name: "indebtedGuardians",
    label: "Are any of the Guardians indebted to the Estate?",
    showExplanationWhenYes: true,
  },
  {
    name: "replacingFiduciary",
    label: "Does applicant replace any previous fiduciary?",
    showExplanationWhenYes: true,
  },
  {
    name: "replacingPriorBond",
    label: "Does this bond replace any prior bond?",
    showExplanationWhenYes: true,
  },
  {
    name: "isDeclined",
    label:
      "Has the applicant for this bond been declined by another Surety?",
    showExplanationWhenYes: true,
  },
  {
    name: "demandedByInterestedParty",
    label:
      "Is this bond required on the demand of an interested party other than the Court?",
    showExplanationWhenYes: true,
  },
  {
    name: "outstandingLawsuit",
    label:
      "Does the applicant have any outstanding lawsuits, judgment, or liens?",
    showExplanationWhenYes: true,
    explanationKey: "outstandingLawsuitExplanation",
  },
  {
    name: "bankruptOrInsolvent",
    label: "Has the applicant ever been bankrupt or insolvent?",
    showExplanationWhenYes: true,
    explanationKey: "bankruptOrInsolventExplanation",
  },
  {
    name: "jointControlExercised",
    label: "Will joint control be exercised?",
    showExplanationWhenYes: true,
  },
  {
    name: "supplementalNeedsTrust",
    label: "Will a supplemental needs trust be created?",
  },
];

/** @type {YesNoQuestion[]} */
const guardianshipRetrievalQuestions = guardianshipCreationQuestions.map(
  (question) =>
    question.name === "didLastWillAndTestament"
      ? { ...question, required: true }
      : question
);

/** @type {Record<string, string>} */
const guardianshipExplanationDefaults = {
  ongoingBusiness: "",
  didLastWillAndTestament: "",
  indebtedGuardians: "",
  replacingFiduciary: "",
  replacingPriorBond: "",
  demandedByInterestedParty: "",
  jointControlExercised: "",
  supplementalNeedsTrust: "",
  outstandingLawsuitExplanation: "",
  bankruptOrInsolventExplanation: "",
  isDeclined: "",
};

/** @type {YesNoQuestion[]} */
const trusteeSharedQuestions = [
  {
    name: "ongoingBusiness",
    label:
      "Will any of the business of the wards be operated by the trustee?",
    showExplanationWhenYes: true,
  },
  {
    name: "indebtedGuardians",
    label: "Are any of the trustees indebted to the Estate?",
    showExplanationWhenYes: true,
  },
  {
    name: "replacingPriorBond",
    label: "Does this bond replace any prior bond?",
    showExplanationWhenYes: true,
  },
  {
    name: "supplementalNeedsTrust",
    label: "Will a supplemental needs trust be created?",
  },
];

/** @type {Record<string, string>} */
const trusteeExplanationDefaults = {
  ongoingBusiness: "",
  indebtedGuardians: "",
  replacingPriorBond: "",
  supplementalNeedsTrust: "",
  isDeclined: "",
};

/** @type {YesNoQuestion[]} */
const probateRetrievalQuestions = [
  {
    name: "ongoingBusiness",
    label:
      "Will any of the business of the deceased be operated by the fiduciary?",
    showExplanationWhenYes: true,
  },
  {
    name: "didLastWillAndTestament",
    label: "Did deceased execute a last will and testament?",
  },
  {
    name: "indebtedGuardians",
    label: "Are any of the principals indebted to the Estate?",
    showExplanationWhenYes: true,
  },
  {
    name: "replacingFiduciary",
    label: "Does applicant replace any previous fiduciary?",
    showExplanationWhenYes: true,
  },
  {
    name: "replacingPriorBond",
    label: "Does this bond replace any prior bond?",
  },
  {
    name: "isDeclined",
    label:
      "Has the applicant for this bond been declined by another Surety?",
    showExplanationWhenYes: true,
  },
  {
    name: "demandedByInterestedParty",
    label:
      "Is this bond required on the demand of an interested party other than the Court?",
    showExplanationWhenYes: true,
  },
  {
    name: "outstandingLawsuit",
    label:
      "Does the applicant have any outstanding lawsuits, judgment, or liens?",
    showExplanationWhenYes: true,
    explanationKey: "outstandingLawsuitExplanation",
  },
  {
    name: "bankruptOrInsolvent",
    label: "Has the applicant ever been bankrupt or insolvent?",
    showExplanationWhenYes: true,
    explanationKey: "bankruptOrInsolventExplanation",
  },
  {
    name: "anyDisputes",
    label: "Any disputes between heirs?",
    showExplanationWhenYes: true,
  },
];

/** @type {Record<string, string>} */
const probateExplanationDefaults = {
  ongoingBusiness: "",
  didLastWillAndTestament: "",
  indebtedGuardians: "",
  replacingFiduciary: "",
  replacingPriorBond: "",
  demandedByInterestedParty: "",
  jointControlExercised: "",
  supplementalNeedsTrust: "",
  outstandingLawsuitExplanation: "",
  bankruptOrInsolventExplanation: "",
  isDeclined: "",
  anyDisputes: "",
};

export const GENERAL_INFORMATION_CONFIG = {
  va: {
    creation: {
      scrollToTop: true,
      showErrors: true,
      multilineExplanations: false,
      explanationDefaults: vaExplanationDefaults,
      questions: vaQuestions,
      authorization: authorizationCreation,
      authorizationBanner: AUTHORIZATION_BANNER,
    },
    retrieval: {
      scrollToTop: false,
      showErrors: false,
      multilineExplanations: true,
      explanationDefaults: vaExplanationDefaults,
      questions: vaQuestions,
      authorization: authorizationRetrieval250k,
      authorizationBanner: AUTHORIZATION_BANNER,
    },
  },
  guardianship: {
    creation: {
      scrollToTop: true,
      showErrors: true,
      multilineExplanations: false,
      explanationDefaults: {
        ...guardianshipExplanationDefaults,
        anyDisputes: "",
        requestForAuthorization: "",
      },
      questions: guardianshipCreationQuestions,
      authorization: null,
      authorizationBanner: AUTHORIZATION_BANNER,
    },
    retrieval: {
      scrollToTop: false,
      showErrors: false,
      multilineExplanations: true,
      explanationDefaults: guardianshipExplanationDefaults,
      questions: guardianshipRetrievalQuestions,
      authorization: authorizationGuardianshipRetrieval,
      authorizationBanner: AUTHORIZATION_BANNER,
    },
  },
  trustee: {
    creation: {
      scrollToTop: true,
      showErrors: true,
      multilineExplanations: false,
      explanationDefaults: trusteeExplanationDefaults,
      citizenshipQuestion: {
        defaultValue: "Yes",
        showWarningOnNo: true,
        navigateOnNo: true,
      },
      questions: trusteeSharedQuestions,
      authorization: authorizationCreation,
      authorizationBanner: AUTHORIZATION_BANNER,
    },
    retrieval: {
      scrollToTop: false,
      showErrors: false,
      multilineExplanations: true,
      explanationDefaults: trusteeExplanationDefaults,
      citizenshipQuestion: {
        defaultValue: "No",
        showWarningOnNo: false,
        navigateOnNo: false,
      },
      questions: trusteeSharedQuestions,
      authorization: authorizationRetrieval250k,
      authorizationBanner: AUTHORIZATION_BANNER,
    },
  },
  probate: {
    retrieval: {
      scrollToTop: false,
      showErrors: false,
      multilineExplanations: true,
      estimatedAssetsField: true,
      explanationDefaults: probateExplanationDefaults,
      questions: probateRetrievalQuestions,
      authorization: authorizationRetrieval250k,
      authorizationBanner: AUTHORIZATION_BANNER,
    },
  },
};
